import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
  type Sale,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useBusinessPath } from '../../lib/auth';
import { money } from '../../lib/format';
import { useModules } from '../../lib/modules';
import { TRANSFER_REFERENCE_HINT, needsConfirmation, needsTransferReference, transferReferenceReady } from '../../lib/till';
import { Button, Input, Select } from '../../components/ui';

/** Recording money received against an existing, partially-paid sale. */
export function RecordPaymentForm({
  sale,
  onDone,
  onCancel,
}: {
  sale: Sale;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const path = useBusinessPath();
  // A pharmacy believes money other than cash once somebody has seen it arrive.
  const pharmacy = useModules().active.has('pharmacy');

  // Defaulting to the full outstanding balance covers the common case — the
  // customer has come to clear their debt — in one tap.
  const [amount, setAmount] = useState(sale.amountOutstanding);
  // Credit is what was owed, never how it is paid.
  const [method, setMethod] = useState<PaymentMethod>(sale.paymentMethod === 'credit' ? 'cash' : sale.paymentMethod);
  const [reference, setReference] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const confirmPayment = pharmacy && needsConfirmation(method);
  const referencePayment = pharmacy && needsTransferReference(method);
  const blocked =
    pharmacy && method === 'credit'
      ? 'Credit is not a payment. Choose how the money was paid.'
      : referencePayment && !transferReferenceReady(reference)
        ? 'Enter the reference on the bank alert. One transfer pays once.'
        : confirmPayment && !confirmed
          ? 'Tick that you have seen the slip or the bank alert.'
          : null;
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const record = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.post(path(`/sales/${sale.id}/payments`), payload),
    onSuccess: onDone,
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not record the payment.');
      }
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    if (blocked) {
      setError(blocked);
      return;
    }
    record.mutate({
      amount,
      method,
      ...(reference ? { reference } : {}),
      ...(confirmPayment ? { confirmed } : {}),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <dl className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
        <div>
          <dt className="text-slate-500">Sale total</dt>
          <dd className="mt-0.5 font-medium tabular text-slate-900">
            {money(sale.amount, sale.currencyCode)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Already paid</dt>
          <dd className="mt-0.5 font-medium tabular text-slate-900">
            {money(sale.amountPaid, sale.currencyCode)}
          </dd>
        </div>
        <div className="col-span-2 border-t border-slate-200 pt-2">
          <dt className="text-slate-500">Outstanding</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular text-amber-700">
            {money(sale.amountOutstanding, sale.currencyCode)}
          </dd>
        </div>
      </dl>

      {error && !Object.keys(fieldErrors).length && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Input
        label={`Amount received (${sale.currencyCode})`}
        required
        autoFocus
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        error={fieldErrors.amount}
        hint={`Cannot exceed ${money(sale.amountOutstanding, sale.currencyCode)}`}
      />

      <Select
        label="Method"
        required
        options={PAYMENT_METHODS.map((value) => ({ value, label: PAYMENT_METHOD_LABELS[value] }))}
        value={method}
        onChange={(event) => {
          setMethod(event.target.value as PaymentMethod);
          setConfirmed(false);
        }}
        error={fieldErrors.method}
      />

      <Input
        label="Reference"
        value={reference}
        onChange={(event) => setReference(event.target.value)}
        error={fieldErrors.reference}
        hint={
          referencePayment
            ? `Required — the session ID or reference on the bank alert. ${TRANSFER_REFERENCE_HINT}`
            : 'Optional — transfer reference, receipt number'
        }
      />

      {confirmPayment && (
        <label className="flex items-start gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="mt-1"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          I have seen the approval (terminal slip / bank alert)
        </label>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={record.isPending}>
          Record payment
        </Button>
      </div>
    </form>
  );
}
